// Which permissions Settings → Roles shows a store, and how ticking them
// changes a role. A role is saved as permission ids: a group (parent
// permission) and its children. A role holding any child also holds the group.

export const SHIPPER_PERMISSION_ROOTS = [
  "shipper.access.shipment",
  // "shipper.access.invoice",  // no /Invoices route registered
  // "shipper.access.pickup",   // no /Pickup route registered
  // "shipper.access.tracking", // no /Tracking route registered
  "shipper.access.product",
  // "shipper.access.carrier", // hidden for now
  "shipper.access.order",
  "shipper.access.payment",
  "shipper.access.warehouse",
  "shipper.access.runs",
];

// The children that show. In the older groups only `.view` shows, and ticking
// it ticks the group's hidden children too (view = the whole module). Prices,
// customer details and what a role may do to orders (edit, change the
// fulfilment status, cancel, archive, delete) show on their own so a role can
// leave them out.
export const SHIPPER_ALLOWED_CHILDREN = [
  "shipper.access.shipment.view",
  // "shipper.access.invoice.view",  // no /Invoices route registered
  // "shipper.access.pickup.view",   // no /Pickup route registered
  // "shipper.access.tracking.view", // no /Tracking route registered
  "shipper.access.product.view",
  // "shipper.access.carrier.view", // hidden for now
  "shipper.access.order.view",
  "shipper.access.order.price",
  "shipper.access.order.customer",
  "shipper.access.order.edit",
  "shipper.access.order.status",
  "shipper.access.order.cancel",
  "shipper.access.order.archive",
  "shipper.access.order.delete",
  "shipper.access.payment.view",
];

// Groups whose every child shows and is ticked on its own.
export const SHIPPER_OPEN_ROOTS = [
  "shipper.access.warehouse",
  "shipper.access.runs",
];

// Keep hidden children in the data (marked `hidden: true`) so toggling a
// visible permission can also toggle its hidden siblings.
export const filterPermissionTree = (
  allPermissions,
  roots,
  allowedChildren,
  openRoots = [],
) =>
  allPermissions
    .filter((p) => roots.includes(p.name))
    .map((p) => ({
      ...p,
      children: (p.children ?? []).map((c) => ({
        ...c,
        hidden: !openRoots.includes(p.name) && !allowedChildren.includes(c.name),
      })),
    }));

const idsOf = (list) => (list ?? []).map((p) => p.id);
const withIds = (selected, ids) => Array.from(new Set([...selected, ...ids]));
const withoutIds = (selected, ids) => selected.filter((id) => !ids.includes(id));

export const groupIds = (group) => [group.id, ...idsOf(group.children)];

export const visibleChildren = (group) =>
  (group.children ?? []).filter((c) => !c.hidden);

// A group with one child showing needs no child rows: its own box does the same.
export const hasChildRows = (group) => visibleChildren(group).length > 1;

export const toggleGroup = (selected, group, on) =>
  on ? withIds(selected, groupIds(group)) : withoutIds(selected, groupIds(group));

// A `.view` child brings the group's hidden children with it; any other child
// is ticked on its own. The group is on while any of its children is.
export const toggleChild = (selected, group, child, on) => {
  const hidden =
    child.name === `${group.name}.view`
      ? idsOf((group.children ?? []).filter((c) => c.hidden))
      : [];
  const ids = [child.id, ...hidden];
  const next = on ? withIds(selected, ids) : withoutIds(selected, ids);
  const anyChild = idsOf(group.children).some((id) => next.includes(id));
  return anyChild ? withIds(next, [group.id]) : withoutIds(next, [group.id]);
};

// The group's box. With child rows: ticked when every child showing is ticked,
// part-ticked when some are. Without: ticked when the role holds any of it.
export const groupState = (selected, group) => {
  if (!hasChildRows(group)) {
    return {
      checked: groupIds(group).some((id) => selected.includes(id)),
      indeterminate: false,
    };
  }
  const shown = visibleChildren(group);
  const on = shown.filter((c) => selected.includes(c.id)).length;
  return { checked: on === shown.length, indeterminate: on > 0 && on < shown.length };
};

// Sets a checkbox's part-ticked look, which has no HTML attribute.
export const indeterminateRef = (indeterminate) => (el) => {
  if (el) el.indeterminate = indeterminate;
};
